import { useEffect, useRef, useState } from 'react';
import { useSettingsStore } from '@/stores/settingsStore';
import { LANG_LABEL, LANG_FLAG, type Lang } from '@/i18n/translations';

const LANGS: Lang[] = ['en', 'vi', 'zh', 'ja', 'ko', 'th'];

/**
 * Compact language switcher: a flag button that opens a flyout list. Used in
 * places where the full SettingsScreen LanguagePicker grid would be too large
 * (HomeScreen header, login screen corner).
 */
export function LangSwitcher() {
  const lang = useSettingsStore((s) => s.lang);
  const setLang = useSettingsStore((s) => s.setLang);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Change language"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid var(--hairline)',
          background: 'rgba(0,0,0,0.35)',
          color: 'var(--ink)',
          fontFamily: 'var(--f-display)',
          fontSize: 11,
          letterSpacing: '0.06em',
          cursor: 'pointer',
          minHeight: 32,
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>{LANG_FLAG[lang]}</span>
        <span>{lang.toUpperCase()}</span>
        <span style={{ fontSize: 9, opacity: 0.6 }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 160,
            padding: 4,
            background: 'rgba(14,11,46,0.98)',
            border: '1px solid var(--hairline)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 50,
          }}
        >
          {LANGS.map((l) => {
            const active = l === lang;
            return (
              <button
                key={l}
                onClick={() => {
                  setLang(l);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: active ? 'rgba(34,211,238,0.18)' : 'transparent',
                  color: active ? 'var(--c-cyan)' : 'var(--ink)',
                  fontFamily: 'var(--f-display)',
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 16 }}>{LANG_FLAG[l]}</span>
                <span>{LANG_LABEL[l]}</span>
                {active && <span style={{ marginLeft: 'auto', fontSize: 12 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
